import { useEffect } from 'react';

import type { TierGroupChildIdsByParentId } from 'features/offer/utils/personalize-offer-list-items';

import { RunningExperiments, useExperiments } from 'state/experiments';

import { useOffersPersonalizationTierGroups } from './use-offers-personalization-tier-groups';

export interface IOffersPersonalizationExperiment {
  isPending: boolean;
  isTreatment: boolean;
  tierGroupChildIdsByParentId: TierGroupChildIdsByParentId;
  tierGroupLookupHasError: boolean;
  sortedSystemwideOffers?: string[];
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

export const parseSortedSystemwideOffers = (payload: unknown): string[] | undefined => {
  if (!payload || typeof payload !== 'object') {
    return;
  }

  const value = (payload as Record<string, unknown>).sortedSystemwideOffers;

  if (!Array.isArray(value)) {
    return;
  }

  const ids = Array.from(new Set(value.filter(isNonEmptyString)));
  return ids.length > 0 ? ids : undefined;
};

export const useOffersPersonalizationExperiment = (): IOffersPersonalizationExperiment => {
  const { getVariantValue, isAssignmentLoading, logExposure } = useExperiments();

  const variant = getVariantValue(RunningExperiments.OffersPersonalization);
  const variantValue = variant?.value;

  const isExperimentOff = variant?.key === 'off';

  const isTreatmentAssignment = !isExperimentOff && !!variantValue && variantValue !== 'control';

  const sortedSystemwideOffers = isTreatmentAssignment
    ? parseSortedSystemwideOffers(variant?.payload)
    : undefined;

  const isTreatment =
    !isAssignmentLoading && isTreatmentAssignment && Boolean(sortedSystemwideOffers);

  const {
    childIdsByParentId: tierGroupChildIdsByParentId,
    hasError: tierGroupLookupHasError,
    isPending: isTierGroupLookupPending,
  } = useOffersPersonalizationTierGroups(isTreatment, sortedSystemwideOffers);

  useEffect(() => {
    if (isAssignmentLoading || isExperimentOff || !variantValue) {
      return;
    }

    logExposure(RunningExperiments.OffersPersonalization);
  }, [isAssignmentLoading, isExperimentOff, logExposure, variantValue]);

  if (isAssignmentLoading || isTierGroupLookupPending) {
    return {
      isPending: true,
      isTreatment: false,
      tierGroupChildIdsByParentId,
      tierGroupLookupHasError,
    };
  }

  if (isTreatment && sortedSystemwideOffers) {
    return {
      isPending: false,
      isTreatment: true,
      sortedSystemwideOffers,
      tierGroupChildIdsByParentId,
      tierGroupLookupHasError,
    };
  }

  return {
    isPending: false,
    isTreatment: false,
    tierGroupChildIdsByParentId,
    tierGroupLookupHasError,
  };
};
